import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuList } from "@/modern/components/swivu-list";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useState, useEffect, useMemo } from "react";
import { ColorPicker } from "@/modern/components/color-picker";
import { Team } from "@/bryntum-lib/models/Team";
import { toast } from "sonner";
import { GRANTS } from "@/other/constants/PrivilegeTypes";

const getRandomColor = () => {
  return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
}

const TeamDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user } = useSwivuBryntumContext();
  const [teams, setTeams] = useState<Team[]>([]);

  const canEdit = useMemo<boolean>(() => {
    if (!user) return false;
    return user.can(GRANTS.grant_one);
  }, [user])

  const { subscribe } = useSwivuStore({
    storeId: STORE_KEYS.boardTeamsStore,
  })

  const loadTeams = () => {
    if (!project) return;
    const teamStore = project.teamStore;
    const teamRecords = teamStore.query(() => true, true) || [];
    setTeams(() => teamRecords);
  }

  useEffect(() => {
    loadTeams();
    return subscribe(() => {
      loadTeams();
    })
  }, [subscribe]);

  const isDuplicateTeamName = (name: string, excludeTeamId?: string | number) => {
    const teamStore = project.teamStore;
    const allTeams = teamStore.query(() => true, true) || [];
    return allTeams.some(t => {
      if (excludeTeamId !== undefined && t.id === excludeTeamId) {
        return false;
      }
      return t.name.toLowerCase() === name.toLowerCase();
    });
  };

  const handleAddItem = (value: string) => {
    if (!value.trim()) {
      return;
    }

    if (isDuplicateTeamName(value)) {
      toast.error('Team name already exists');
      return;
    }

    const teamStore = project.teamStore;
    const newTeam = teamStore.createRecord({
      name: value,
      color: getRandomColor(),
    });
    teamStore.add(newTeam);
  }

  const handleReorderItems = (movedItem: Team, beforeItem: Team | null) => {
    const teamsStore = project.teamStore;
    // @ts-expect-error - team is a Team model
    teamsStore.move(movedItem, beforeItem);
  }

  const handleUpdateTeamName = (team: Team, newName: string) => {
    if (!newName.trim()) {
      return false;
    }

    if (isDuplicateTeamName(newName, team.id)) {
      toast.error('Team name already exists');
      return false;
    }

    team.name = newName;
    return true;
  }

  const handleDeleteTeam = (team: Team) => {
    const teamsStore = project.teamStore;
    // @ts-expect-error - team is a Team model
    teamsStore.remove(team);
  }

  return (
    <div className="p-6 flex flex-col max-w-3xl w-full h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <SwivuList.Root<Team>
        items={teams}
        onReorder={handleReorderItems}
        searchConfig={{
          fields: ["name", "color"],
          placeholder: "Search teams...",
        }}
      >
        <SwivuList.Toolbar />

        <SwivuList.Content<Team> emptyMessage="No teams yet. Create your first team!">
          {(team: Team) => (
            <SwivuList.Item value={team.id as string | number}>
              {/* Drag Handle */}
              <SwivuList.ItemHandle disabled={!canEdit} value={team.id as string | number} />

              {/* Team Name */}
              <div className="flex-1 min-w-0 flex items-center gap-2">
                {/* Team Color */}
                <div className="flex items-center">
                  <ColorPicker
                    value={team.color}
                    onChange={(color) => {
                      team.color = color;
                    }}
                    clearable={true}
                    triggerClassName="h-auto w-auto [&_*]:m-0 py-0 border-none px-0 bg-transparent hover:opacity-80"
                    disabled={!canEdit}
                    hideDisplayValue={true}
                  />
                </div>
                <SwivuList.TextField
                  item={team}
                  value={team.name}
                  onUpdate={handleUpdateTeamName}
                  placeholder="Enter team name"
                  disabled={!canEdit}
                  className="flex-1"
                />
              </div>


              {/* Actions */}
              <SwivuList.ItemActions
                disabled={!canEdit}
                onDelete={() => handleDeleteTeam(team)}
              />
            </SwivuList.Item>
          )}
        </SwivuList.Content>

        <SwivuList.QuickAdd
          disabled={!canEdit}
          onAdd={handleAddItem}
          placeholder="Add new team..."
        />
      </SwivuList.Root>
    </div>
  );
};

export default TeamDetail;
